import { DatePipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import {
  catchError,
  combineLatest,
  EMPTY,
  filter,
  finalize,
  map,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import {
  triggerIfSlow,
  WebSocketService,
} from '@ipg/ngx-frontend-toolset';
import { DataTableContainerComponent } from '../../components/data-table-container/data-table-container.component';
import { DialogService } from '../../components/dialog/dialog.service';
import { HeaderComponent } from '../../components/header/header.component';
import { GlobalLoadersService } from '../../global-loaders.service';
import {
  InteractionSession,
  InteractionSessionClosedReason,
  InteractionSessionStatus,
} from '../../models/interactions.model';
import { MapFromPipe } from '../../pipes/map-from.pipe';
import { TableLimitService } from '../../services/table-limit.service';
import { InteractionSessionService } from './services/interaction-session.service';

@Component({
  selector: 'app-interaction-history',
  imports: [
    MatTableModule,
    MatTooltipModule,
    MatPaginator,
    DatePipe,
    DataTableContainerComponent,
    MapFromPipe,
    NgClass,
    MatSortModule,
  ],
  templateUrl: './interaction-history.component.html',
  styleUrl: './interaction-history.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InteractionHistoryComponent {
  public readonly sortingKeys = {
    label: `label`,
    version: `version`,
    status: `status`,
    plannedAt: `plannedAt`,
    startedAt: `startedAt`,
    endedAt: `endedAt`,
    source: `source`,
  };

  private static readonly LOADER_CALLER_ID = 'interaction_history_loader_caller';

  private readonly _dialog = inject(DialogService);
  private readonly _tableLimit = inject(TableLimitService);
  private readonly _interactionSessionService = inject(InteractionSessionService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _wsService = inject(WebSocketService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _globalLoaders = inject(GlobalLoadersService);

  private readonly _sort = signal({
    sortBy: this.sortingKeys.startedAt,
    sortOrder: 'desc',
  });

  private readonly _clientId$ = this._route.parent!.paramMap.pipe(
    map((params) => params.get('clientId')),
    filter((clientId) => clientId != null)
  );

  private readonly _query = signal({
    page: 1,
    limit: this._tableLimit.getLimit(),
  });

  private readonly _refresh$ = new Subject<void>();

  private readonly _params$ = combineLatest({
    clientId: this._clientId$,
    sort: toObservable(this._sort),
    query: toObservable(this._query),
    __refresh: this._refresh$.pipe(startWith(undefined)),
  });

  private readonly _paginator = viewChild.required(MatPaginator);

  private readonly _paginatedInteractionSessions$ = this._params$.pipe(
    switchMap(({ clientId, query, sort }) =>
      this._interactionSessionService
        .getAll(clientId, { params: { ...query, ...sort } })
        .pipe(
          tap({
            error: () =>
              this._dialog.alert({
                title: `Foutmelding`,
                message: `Er is iets misgegaan bij het ophalen van de historie. Probeer het later nog eens`,
                confirmText: `OK`,
              }),
          }),
          takeUntilDestroyed(this._destroyRef),
          triggerIfSlow(() =>
            this._globalLoaders.setActive(
              InteractionHistoryComponent.LOADER_CALLER_ID,
              HeaderComponent.LOADER_ID
            )
          ),
          finalize(() =>
            this._globalLoaders.setInactive(
              InteractionHistoryComponent.LOADER_CALLER_ID,
              HeaderComponent.LOADER_ID
            )
          )
        )
    ),
    tap(({ meta }) => this._setPaginator({ page: meta.page, total: meta.total })),
    catchError(() => EMPTY)
  );

  private readonly _interactionSessions$ = this._paginatedInteractionSessions$.pipe(
    map(({ items }) => items)
  );

  protected readonly dataSource = signal<InteractionSession[]>([]);
  protected readonly hasNoData = computed(() => this.dataSource().length === 0);

  protected readonly pageSize = computed(() => this._query().limit);

  protected readonly statusMap: Record<InteractionSessionStatus, string> = {
    cancelled: 'Niet afgehandeld',
    completed: 'Afgehandeld',
    expired: 'Verlopen',
    processing: 'In behandeling',
    pending: 'In afwachting',
  };

  protected readonly closedReasonMap: Record<InteractionSessionClosedReason, string> = {
    completed: 'Afgerond',
    missed: 'Gemist',
    stopped: 'Automatisch onderbroken',
    manual_stop: 'Handmatig onderbroken',
    goto_interaction: 'Ga naar interactie',
    activity_timeout: 'Geen activiteit',
    cancelled: 'Geannuleerd',
    error: 'Error',
  };

  protected readonly displayedColumns = [
    'name',
    'version',
    'status',
    'closedReason',
    'source',
    'startedAt',
    'endedAt',
    'plannedAt',
  ];

  constructor() {
    this._interactionSessions$.pipe(takeUntilDestroyed()).subscribe((sessions) => {
      this.dataSource.set(sessions);
    });

    this._clientId$.pipe(takeUntilDestroyed()).subscribe((clientId) => {
      this._setupWebSocket(clientId);
    });
  }

  handleSortChange({ active, direction }: Sort) {
    this._sort.update((currentState) => ({
      ...currentState,
      sortBy: active,
      sortOrder: direction,
    }));
  }

  handlePageChange({ pageIndex, pageSize }: PageEvent) {
    this._tableLimit.saveLimit(pageSize);
    this._query.update((currentState) => ({
      ...currentState,
      page: pageIndex + 1,
      limit: pageSize,
    }));
  }

  /**
   * SCM media sessions carry a placeholder interaction whose internal label
   * ("SCM Media Playback") means nothing to a caregiver — show what actually
   * happened on the robot instead.
   */
  protected getInteractionName(session: InteractionSession): string {
    if (session.isScmMedia) return 'Signaleringsmodule mediaweergave';

    return session.interaction.label;
  }

  protected getClosedReasonName(reason: string | null): string {
    if (null === reason) return 'Nog niet afgerond';
    if (this._isClosedReason(reason)) return this.closedReasonMap[reason];

    return 'Andere reden';
  }

  protected getClosedReasonBadge(reason: string | null): string {
    if (null === reason) return 'bg-gray-100 text-gray-950';
    if (['stopped', 'manual_stop', 'cancelled', 'error'].includes(reason))
      return 'bg-orange-100 text-orange-950';
    if (reason === 'activity_timeout') return 'bg-red-100 text-red-950';
    if (reason === 'missed') return 'bg-blue-100 text-blue-950';
    if (['completed', 'goto_interaction'].includes(reason))
      return 'bg-green-100 text-green-950';
    return 'bg-gray-100 text-gray-950';
  }

  private _isClosedReason(reason: string): reason is InteractionSessionClosedReason {
    return reason in this.closedReasonMap;
  }

  private _setPaginator({ page, total }: { page: number; total: number }) {
    const paginator = this._paginator();

    paginator.pageIndex = page - 1;
    paginator.length = total;
  }

  private _setupWebSocket(clientId: string): void {
    this._wsService
      .connect('/event-stream', {
        requireAuth: true,
        topics: [`client/${clientId}/interaction-sessions`],
      })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (connection) => {
          connection.events$.subscribe(() => {
            this._refresh$.next();
          });
        },
        error: (error) =>
          console.error('WebSocket connection failed for interaction sessions:', error),
      });
  }
}
